import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BackLink from "@/components/BackLink.jsx";
import Icon from "@/components/icons/Icon.jsx";
import { checkoutOrder, getAddresses, getUser } from "@/api/customer.api.js";
import { useCart } from "@/cart/CartContext.jsx";
import { useAuth } from "@/auth/AuthContext.jsx";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import {
    CART_ROUTE,
    CUSTOMER_PROFILE_ROUTE,
    ORDER_ROUTE,
    ROLES,
    TERMS_ROUTE
} from "@/utils/constants.js";
import { CUSTOMER_PROFILE_DEFAULT_SECTION } from "@/utils/profileMenus.js";
import Price from "@/components/Price.jsx";
import ProductRecommendations from "@/components/ProductRecommendations.jsx";
import { formatShopOptionLabel, getActiveShops } from "@/utils/storeAddress.js";
import "@/pages/product/order.css";

export default function CheckoutPage() {
    const { storeInfo } = useStoreInfo();
    const [fulfillmentType, setFulfillmentType] = useState("PICKUP");
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState("");
    const [selectedShopLocationId, setSelectedShopLocationId] = useState("");
    const [customerComment, setCustomerComment] = useState("");
    const [contactPhones, setContactPhones] = useState([]);
    const [contactEmail, setContactEmail] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { items, refreshCart, setItems } = useCart();
    const { isAuthenticated, role } = useAuth();
    const navigate = useNavigate();

    const cartTotal = items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const activeShops = getActiveShops(storeInfo?.shops);
    const hasContactDetails = contactPhones.length > 0 || Boolean(contactEmail);

    useEffect(() => {
        if (!isAuthenticated || role !== ROLES.CUSTOMER) {
            return;
        }
        refreshCart().then(() => {});
        getAddresses()
            .then((data) => {
                const list = data.data || [];
                setAddresses(list);
                if (list.length > 0) {
                    setSelectedAddressId(list[0].id);
                }
            })
            .catch(() => {});
        getUser()
            .then((user) => {
                const phones = (user.phones || [])
                    .map((phone) => ({
                        phoneNumber: phone.phoneNumber?.trim() || "",
                        label: phone.label?.trim() || ""
                    }))
                    .filter((phone) => phone.phoneNumber);
                setContactPhones(phones);
                setContactEmail(user.email?.trim() || "");
            })
            .catch(() => {
                setContactPhones([]);
                setContactEmail("");
            });
    }, [isAuthenticated, role, refreshCart]);

    useEffect(() => {
        if (activeShops.length > 0 && !selectedShopLocationId) {
            setSelectedShopLocationId(activeShops[0].id);
        }
    }, [activeShops, selectedShopLocationId]);

    useEffect(() => {
        if (isAuthenticated && role === ROLES.CUSTOMER && items.length === 0 && !submitting) {
            navigate(CART_ROUTE, { replace: true });
        }
    }, [items.length, isAuthenticated, role, navigate, submitting]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (fulfillmentType === "DELIVERY" && !selectedAddressId) {
            setError("Veuillez sélectionner une adresse ou en ajouter une dans votre compte.");
            return;
        }
        if (fulfillmentType === "PICKUP" && !selectedShopLocationId) {
            setError("Veuillez sélectionner un magasin pour le retrait.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const result = await checkoutOrder({
                fulfillmentType,
                addressId: fulfillmentType === "DELIVERY" ? selectedAddressId : undefined,
                shopLocationId: fulfillmentType === "PICKUP" ? selectedShopLocationId : undefined,
                customerComment: customerComment.trim() || undefined
            });
            setItems([]);
            setMessage("Commande confirmée");
            setTimeout(() => {
                if (result.orderId) {
                    navigate(ORDER_ROUTE(result.orderId));
                } else {
                    navigate(`${CUSTOMER_PROFILE_ROUTE}?section=${CUSTOMER_PROFILE_DEFAULT_SECTION}`);
                }
            }, 900);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="container order-page">
                <BackLink to={CART_ROUTE} className="order-back">Retour au panier</BackLink>
                <h1>Passer commande</h1>

                <form className="card order-card" onSubmit={handleSubmit}>
                    <div className="order-summary">
                        <h2>{items.length} article{items.length > 1 ? "s" : ""} dans le panier</h2>
                        <p className="muted">Total panier : <Price value={cartTotal} /></p>
                    </div>

                    <fieldset className="order-fulfillment">
                        <legend>Mode de réception</legend>
                        <label className="order-fulfillment-option">
                            <input
                                type="radio"
                                name="fulfillmentType"
                                value="PICKUP"
                                checked={fulfillmentType === "PICKUP"}
                                onChange={() => setFulfillmentType("PICKUP")}
                                disabled={submitting}
                            />
                            <span>
                                <strong>Retrait en magasin</strong>
                                <span className="muted order-fulfillment-detail">
                                    Retirer la commande dans l&apos;un de nos magasins
                                </span>
                            </span>
                        </label>
                        <label className="order-fulfillment-option">
                            <input
                                type="radio"
                                name="fulfillmentType"
                                value="DELIVERY"
                                checked={fulfillmentType === "DELIVERY"}
                                onChange={() => setFulfillmentType("DELIVERY")}
                                disabled={submitting}
                            />
                            <span>
                                <strong>Livraison à l&apos;adresse</strong>
                                <span className="muted order-fulfillment-detail">
                                    Garage, atelier ou autre adresse de votre choix.
                                </span>
                            </span>
                        </label>
                    </fieldset>

                    {fulfillmentType === "PICKUP" && (
                        <div className="order-address-picker">
                            {activeShops.length === 0 ? (
                                <p className="muted">Aucun magasin n&apos;est disponible pour le retrait pour le moment.</p>
                            ) : (
                                <label className="field">
                                    Magasin de retrait
                                    <span className="order-select-wrap">
                                        <select
                                            value={selectedShopLocationId}
                                            onChange={(e) => setSelectedShopLocationId(e.target.value)}
                                            disabled={submitting}
                                            required
                                        >
                                            {activeShops.map((shop) => (
                                                <option key={shop.id} value={shop.id}>
                                                    {formatShopOptionLabel(shop)}
                                                </option>
                                            ))}
                                        </select>
                                    </span>
                                </label>
                            )}
                        </div>
                    )}

                    {fulfillmentType === "DELIVERY" && (
                        <div className="order-address-picker">
                            {addresses.length === 0 ? (
                                <div className="order-address-empty">
                                    <p className="muted">
                                        Aucune adresse enregistrée. Ajoutez-en une dans votre compte pour
                                        recevoir la commande (garage, atelier, etc.).
                                    </p>
                                    <Link
                                        to={`${CUSTOMER_PROFILE_ROUTE}?section=addresses`}
                                        className="btn btn-secondary btn-inline"
                                    >
                                        Ajouter une adresse dans mon compte
                                    </Link>
                                </div>
                            ) : (
                                <label className="field">
                                    Adresse de livraison
                                    <span className="order-select-wrap">
                                        <select
                                            value={selectedAddressId}
                                            onChange={(e) => setSelectedAddressId(e.target.value)}
                                            disabled={submitting}
                                            required
                                        >
                                            {addresses.map((address) => (
                                                <option key={address.id} value={address.id}>
                                                    {(address.label ? `${address.label} — ` : "") + address.summary}
                                                </option>
                                            ))}
                                        </select>
                                    </span>
                                </label>
                            )}
                        </div>
                    )}

                    {hasContactDetails && (
                        <Link
                            to={`${CUSTOMER_PROFILE_ROUTE}?section=profile`}
                            className="order-contact-summary"
                            aria-label="Voir ou modifier vos coordonnées dans votre profil"
                        >
                            <span className="order-contact-summary-icon" aria-hidden="true">
                                <Icon name="user" className="order-contact-summary-icon-svg" color="currentColor" />
                            </span>
                            <span className="order-contact-summary-body">
                                {contactPhones.length > 0 ? (
                                    contactPhones.map((phone, index) => (
                                        <span
                                            key={`${phone.phoneNumber}-${index}`}
                                            className="order-contact-summary-primary"
                                        >
                                            {phone.phoneNumber}
                                            {phone.label ? (
                                                <span className="order-contact-summary-phone-label">
                                                    {" "}
                                                    ({phone.label})
                                                </span>
                                            ) : null}
                                        </span>
                                    ))
                                ) : (
                                    <span className="order-contact-summary-primary muted">
                                        Aucun téléphone enregistré
                                    </span>
                                )}
                                {contactEmail ? (
                                    <span className="order-contact-summary-secondary">
                                        {contactEmail}
                                    </span>
                                ) : null}
                            </span>
                            <span className="order-contact-summary-chevron" aria-hidden="true">
                                ›
                            </span>
                        </Link>
                    )}

                    <label className="field">
                        Commentaire (optionnel)
                        <textarea
                            rows="3"
                            value={customerComment}
                            onChange={(e) => setCustomerComment(e.target.value)}
                            placeholder="Instructions de livraison, préférences, etc."
                            maxLength={1000}
                            disabled={submitting}
                        />
                    </label>

                    <p className="order-total">
                        Total : <strong><Price value={cartTotal} /></strong>
                    </p>

                    {message && <p className="success">{message}</p>}
                    {error && <p className="error">{error}</p>}

                    <div className="order-actions">
                        <button
                            className="btn btn-primary btn-inline"
                            type="submit"
                            disabled={
                                submitting
                                || items.length === 0
                                || (fulfillmentType === "PICKUP" && activeShops.length === 0)
                                || (fulfillmentType === "DELIVERY" && addresses.length === 0)
                            }
                        >
                            {submitting ? "Traitement..." : "Confirmer la commande"}
                        </button>
                    </div>

                    <p className="order-terms-note">
                        En cliquant sur le bouton, vous acceptez les{" "}
                        <Link to={TERMS_ROUTE} target="_blank" rel="noopener noreferrer">
                            conditions générales d&apos;utilisation
                        </Link>
                        .
                    </p>
                </form>

                <ProductRecommendations
                    excludeCart
                    resetKey={items.map((item) => item.productId).join(",")}
                />
        </main>
    );
}
